import { useLazyQuery } from "@apollo/client";
import { useFocusEffect } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import {
  Card,
  ModalCloseButton,
  Page,
  Stack,
  TileButtonActionList,
} from "@towersystems/roam-common-native";
import { TerminalClosure } from "@towersystems/roam-common/lib/generated-types";
import dayjs from "dayjs";
import { useCallback, useEffect } from "react";
import { ShiftRootParamList } from "../../../../types";
import { QUERY_TERMINAL_SHIFT } from "../../../../utilities/terminal-shift/constants";
import { CashMovementList } from "../../components/CashMovementList";
import { CountedPaymentList } from "../../components/CountedPaymentList";
import { dateFormat, timeFormat } from "../../components/ShiftListItem";
import { ShiftTotalsSummary } from "../../components/ShiftTotalsSummary";
import { useTranslation } from "../../../../utilities/i18n";
import { Ionicons } from "@expo/vector-icons";
import { Colors } from "react-native-ui-lib";
import { useLlmQuickWinsProvider } from "../../../../utilities/llm-quick-wins";

export type ClosedShiftSummaryScreenProps = StackScreenProps<
  ShiftRootParamList,
  "ClosedShiftSummaryScreen"
>;

export const ClosedShiftSummaryScreen = ({
  route,
  navigation,
}: ClosedShiftSummaryScreenProps) => {
  const { t } = useTranslation("end_of_shift");
  const id = route.params.shiftId;
  const { strategyEnabled } = useLlmQuickWinsProvider();

  const [q, { loading, data, error }] = useLazyQuery<{
    terminalClosure: TerminalClosure;
  }>(QUERY_TERMINAL_SHIFT);

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <ModalCloseButton
          type="Back"
          onPress={() => navigation.navigate("ShiftHistoryScreen")}
        />
      ),
    });
  }, [navigation]);

  useFocusEffect(
    useCallback(() => {
      q({
        variables: {
          id: id,
        },
      }).catch((err) => console.log(err));
    }, [id])
  );

  if (loading) return <Page title="Loading..."></Page>;

  if (error || !data) return <Page title="Error..."></Page>;

  const item = data.terminalClosure;

  const closedDate = dayjs(item.terminalCloseDate).format(dateFormat);
  const closedTime = dayjs(item.terminalCloseDate).format(timeFormat);
  const openedDate = dayjs(item.terminalOpenDate).format(dateFormat);
  const openedTime = dayjs(item.terminalOpenDate).format(timeFormat);

  const title = t("closed_string", {
    date: closedDate,
    time: closedTime,
  });

  const subtitle = t("opened_string", {
    date: openedDate,
    time: openedTime,
    terminal: item.terminalName,
  });

  const actions = [
    {
      id: "1",
      isAction: true,
      label: t("action.email_receipt.label"),
      onPress: () => {
        navigation.navigate("ShiftSendReportModalScreen", {
          shiftId: id,
        });
      },
    },
  ];

  if (strategyEnabled("sales_period_summary_quick_win")) {
    actions.push({
      id: "2",
      isAction: true,
      icon: <Ionicons name="sparkles" size={16} color={Colors.action} />,
      label: "AI Summarize Shift",
      onPress: () => {
        navigation.navigate("AiInsightsRootModalScreen", {
          screen: "AiInsightsScreen",
          startDate: item.terminalOpenDate,
          endDate: item.terminalCloseDate,
        });
      },
    });
  }

  return (
    <Page title={title} secondaryTitle={subtitle}>
      <Stack vertical>
        <TileButtonActionList actions={actions} />
        <Card title={t("card.summary.title")}>
          <ShiftTotalsSummary item={item.saleSummary} />
        </Card>
        <Card title={t("card.payments.title")}>
          <CountedPaymentList payments={item.countedPaymentsSummary || []} />
        </Card>
        <Card title={t("card.movements.title")}>
          <CashMovementList cashMovements={item.cashMovements} />
        </Card>
      </Stack>
    </Page>
  );
};
